"use client";

import React, { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Minus, Square, Copy, X } from "lucide-react";

export function TitleBar() {
  const [platform, setPlatform] = useState<string | null>(null);
  const [isMaximized, setIsMaximized] = useState(false);

  useEffect(() => {
    // Check if we're on Windows
    const checkPlatform = async () => {
      try {
        if (window.electronWindow?.getPlatform) {
          const plat = await window.electronWindow.getPlatform();
          setPlatform(plat);
        }
      } catch (error) {
        console.error("Failed to get platform:", error);
        // Default to showing on Windows if we can't detect
        setPlatform("win32");
      }
    };

    checkPlatform();
  }, []);

  // Only show on Windows
  if (platform !== "win32") {
    return null;
  }

  const handleMinimize = async () => {
    try {
      await window.electronWindow?.minimize();
    } catch (error) {
      console.error("Failed to minimize window:", error);
    }
  };

  const handleMaximize = async () => {
    try {
      await window.electronWindow?.maximize();
      setIsMaximized(!isMaximized);
    } catch (error) {
      console.error("Failed to maximize window:", error);
    }
  };

  const handleClose = async () => {
    try {
      await window.electronWindow?.close();
    } catch (error) {
      console.error("Failed to close window:", error);
    }
  };

  return (
    <div
      className="fixed top-0 left-0 right-0 z-50 flex items-center h-8 bg-background/80 backdrop-blur-sm border-b border-border"
      style={{ 
        WebkitAppRegion: "drag",
        userSelect: "none",
      }}
    >
      {/* Draggable area - takes up remaining space */}
      <div className="flex-1 h-full" style={{ WebkitAppRegion: "drag" }} />
      
      {/* Window controls - not draggable */}
      <div 
        className="flex items-center gap-0 h-full"
        style={{ WebkitAppRegion: "no-drag" }}
      >
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-10 rounded-none hover:bg-muted/50"
          onClick={handleMinimize}
          aria-label="Minimize"
        >
          <Minus className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-10 rounded-none hover:bg-muted/50"
          onClick={handleMaximize}
          aria-label={isMaximized ? "Restore" : "Maximize"}
        >
          {isMaximized ? (
            <Copy className="h-3.5 w-3.5" />
          ) : (
            <Square className="h-3.5 w-3.5" />
          )}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-10 rounded-none hover:bg-destructive/90 hover:text-destructive-foreground"
          onClick={handleClose}
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

